import { z } from "zod";
import type { SimulationAmortissementFormStep } from "./ui-state.js";

const numberLikeSchema = z.union([z.string(), z.number()]).optional();
const consentSchema = z
  .boolean()
  .refine((value) => value, "Le consentement est obligatoire.");

export const simulationAmortissementIntroStepSchema = z.object({});

export const simulationAmortissementBienStepSchema = z
  .object({
    slugParameter: z.enum(["france", "paris"], {
      message: "Le profil de simulation est obligatoire.",
    }),
    prixAchat: numberLikeSchema,
    travaux: numberLikeSchema,
    ameublement: numberLikeSchema,
  })
  .superRefine((input, ctx) => {
    validateRequiredPositive("prixAchat", input.prixAchat, ctx);
    validateOptionalNonNegative("travaux", input.travaux, ctx);
    validateOptionalNonNegative("ameublement", input.ameublement, ctx);
  });

export const simulationAmortissementContactStepSchema = z.object({
  email: z.string().email("Email invalide."),
  firstName: z.string().trim().max(120, "Le prenom est trop long."),
  source: z
    .string()
    .trim()
    .min(1, "La source est obligatoire.")
    .max(120, "La source est trop longue."),
  consent: consentSchema,
  honeypot: z.string().max(0, "Requete invalide.").optional(),
});

export const simulationAmortissementFormSchema = z
  .object({
    email: z.string().email("Email invalide."),
    firstName: z.string().trim().max(120, "Le prenom est trop long."),
    source: z
      .string()
      .trim()
      .min(1, "La source est obligatoire.")
      .max(120, "La source est trop longue."),
    consent: consentSchema,
    honeypot: z.string().max(0).optional(),
    slugParameter: z.enum(["france", "paris"], {
      message: "Le profil de simulation est obligatoire.",
    }),
    prixAchat: numberLikeSchema,
    travaux: numberLikeSchema,
    ameublement: numberLikeSchema,
  })
  .superRefine((input, ctx) => {
    validateRequiredPositive("prixAchat", input.prixAchat, ctx);
    validateOptionalNonNegative("travaux", input.travaux, ctx);
    validateOptionalNonNegative("ameublement", input.ameublement, ctx);
  });

export const simulationAmortissementStepSchemas: Record<
  SimulationAmortissementFormStep,
  z.ZodTypeAny
> = {
  intro: simulationAmortissementIntroStepSchema,
  bien: simulationAmortissementBienStepSchema,
  contact: simulationAmortissementContactStepSchema,
  result: z.object({}),
  "expert-optin": z.object({}),
};

function parseNumberLike(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed =
    typeof value === "number" ? value : Number(value.replaceAll(/\s+/g, ""));
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function validateRequiredPositive(
  fieldName: string,
  value: string | number | undefined,
  ctx: z.RefinementCtx,
): void {
  if (
    value === undefined ||
    value === null ||
    (typeof value === "string" && !value.trim())
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` est obligatoire.`,
    });
    return;
  }

  const parsed = parseNumberLike(value);
  if (parsed === undefined || !Number.isFinite(parsed) || parsed <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` doit etre un nombre positif.`,
    });
  }
}

function validateOptionalNonNegative(
  fieldName: string,
  value: string | number | undefined,
  ctx: z.RefinementCtx,
): void {
  if (
    value === undefined ||
    value === null ||
    (typeof value === "string" && !value.trim())
  ) {
    return;
  }

  const parsed = parseNumberLike(value);
  if (parsed === undefined || !Number.isFinite(parsed) || parsed < 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `\`${fieldName}\` doit etre un nombre positif ou nul.`,
    });
  }
}
